import { PkButton, PkDialog, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useMemo, memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getAppType } from '../../config/appType';
import { useOptions } from '../../hooks';
import { actions } from '../../models';
import { useAppDispatch, useAppSelector } from '../../store';
import type { EditorialElem, PollType } from '../../types';
import { getImageUrlFromArticle, fetchArticleByTag } from '../../utils/apis';
import type { EditorialContent } from '../../utils/utils';

import * as style from './style.module.scss';

const PrizePanel = ({ poll }: { poll: PollType }) => {
  const isVisible = useAppSelector((state) => state.prizeModal.isVisible);
  const dispatch = useAppDispatch();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { promoTag } = useOptions();
  const [article, setArticle] = useState<EditorialContent>(null);

  const appType = useMemo(() => {
    const type = getAppType({ pollType: poll?.type, pollSubtype: poll?.subtype });

    return t(`app_name_${type}`);
  }, [poll, t]);

  useEffect(() => {
    fetchArticleByTag(promoTag, language).then((prizeArticle) => {
      prizeArticle?.id && setArticle(prizeArticle);
    });
  }, [language, promoTag]);

  const bgStyle = useMemo(
    () => ({
      backgroundImage: `url(${getImageUrlFromArticle(article?.photos, 'Square')})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
    }),
    [article?.photos],
  );

  const trackingData = useMemo(
    () => ({
      name: `${appType.toUpperCase()} promo`,
      id: poll.id,
      category: 'sponsor promotion',
    }),
    [appType, poll.id],
  );

  const handleClose = () => {
    dispatch(actions.prizeModal.setVisible(false));
  };

  if (!article) return null;

  return (
    <PkDialog isOpen={isVisible} className={style.promoDialog} onPkClose={handleClose} width='420' height='560'>
      <div style={bgStyle} slot='body'>
        <div className={style.promoDialogWrapper}>
          <div className={style.promoDialogBg}>
            <div dangerouslySetInnerHTML={{ __html: article?.summary }}></div>
            <div className={style.promoDialogHeadline} dangerouslySetInnerHTML={{ __html: article?.headline }}></div>
            <div
              className={style.promoDialogBody}
              dangerouslySetInnerHTML={{
                __html: article?.body?.map((data: EditorialElem) => data._html).join(''),
              }}></div>
            {article?.link && (
              <PkButton
                width='240'
                className={clsx('js-tracking-card', !isVisible && 'hidden')}
                data-tracking={JSON.stringify(trackingData)}
                ariaLabel={article?.link?._text}
                appearance='outlined'
                linkHref={article?.link?._attributes?.href}
                linkRel='noopener'
                linkTarget='_blank'
                type='button'>
                {article?.link?._text}
                <PkIcon name='navigation-leave' class='pk-ml--8 hydrated' />
              </PkButton>
            )}
          </div>
        </div>
      </div>
    </PkDialog>
  );
};
export default memo(PrizePanel);
